import React, { useEffect, useState, useCallback } from 'react';
import MiniERPPriceFlowProductForm from './MiniERPPriceFlowProductForm';
import {
  listProducts, createProduct, updateProduct, deleteProduct, duplicateProduct, reorderProducts,
} from './MiniERPPriceFlowAPI';

const AVAIL_STYLE = {
  Available: 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30',
  'Limited Stock': 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
  'Out of Stock': 'bg-rose-500/15 text-rose-300 ring-rose-500/30',
  'Coming Soon': 'bg-sky-500/15 text-sky-300 ring-sky-500/30',
};

function AvailBadge({ availability }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${AVAIL_STYLE[availability] || AVAIL_STYLE.Available}`}>
      {availability}
    </span>
  );
}

function PriceCell({ price, offerPrice }) {
  if (offerPrice != null && offerPrice < price) {
    return (
      <span>
        <span className="line-through text-white/30 text-xs mr-1.5">₹{price}</span>
        <span className="text-emerald-400 font-semibold">₹{offerPrice}</span>
      </span>
    );
  }
  return <span className="text-white/80 font-semibold">₹{price}</span>;
}

export default function MiniERPPriceFlowProductList({ businessId, onBack }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listProducts({ businessId, search, limit: 100 });
      setItems(data.items);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [businessId, search]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const handleSave = async (form) => {
    if (editing && editing !== 'new') await updateProduct(editing._id, form);
    else await createProduct({ ...form, businessId });
    setEditing(null);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;
    await deleteProduct(id);
    load();
  };

  const handleDuplicate = async (id) => {
    await duplicateProduct(id);
    load();
  };

  const handleToggleActive = async (p) => {
    await updateProduct(p._id, { status: p.status === 'active' ? 'inactive' : 'active' });
    load();
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const reordered = [...items];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setItems(reordered);
    await reorderProducts(businessId, reordered.map((p, i) => ({ id: p._id, displayOrder: i })));
  };

  return (
    <div className="min-h-screen bg-[#0B0B12] text-white px-4 py-6 sm:px-8 sm:py-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button onClick={onBack} className="text-sm text-white/50 hover:text-white transition">← Back</button>
          <h2 className="text-xl font-bold tracking-tight">Products</h2>
          <span className="w-12" />
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products..."
            className="flex-1 bg-white/5 ring-1 ring-white/10 rounded-xl px-3 py-2.5 text-sm placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition"
          />
          <button
            onClick={() => setEditing('new')}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-400 hover:to-emerald-400 shadow-lg shadow-emerald-900/30 transition whitespace-nowrap"
          >
            + Add Product
          </button>
        </div>

        {error && <p className="text-rose-400 text-sm mb-3">{error}</p>}

        {loading ? (
          <p className="text-white/40 text-sm">Loading…</p>
        ) : !items.length ? (
          <div className="text-center py-16 text-white/30 rounded-3xl ring-1 ring-white/5 bg-white/[0.02]">
            No products yet — add your first one to go live.
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto rounded-2xl ring-1 ring-white/10 bg-[#14141C]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-widest text-white/40 border-b border-white/5">
                    <th className="px-4 py-3 font-semibold">Order</th>
                    <th className="px-4 py-3 font-semibold">Product</th>
                    <th className="px-4 py-3 font-semibold">Category</th>
                    <th className="px-4 py-3 font-semibold">Price</th>
                    <th className="px-4 py-3 font-semibold">Availability</th>
                    <th className="px-4 py-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p, i) => (
                    <tr key={p._id} className={`border-b border-white/5 last:border-0 hover:bg-white/[0.03] transition ${p.status === 'inactive' ? 'opacity-40' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-0.5">
                          <button onClick={() => move(i, -1)} disabled={i === 0} className="text-white/30 hover:text-white disabled:opacity-20 text-xs">▲</button>
                          <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-white/30 hover:text-white disabled:opacity-20 text-xs">▼</button>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-medium">
                        {p.productName}
                        {p.offerLabel && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30">{p.offerLabel}</span>}
                      </td>
                      <td className="px-4 py-3 text-white/60">{p.category}</td>
                      <td className="px-4 py-3"><PriceCell price={p.price} offerPrice={p.offerPrice} /></td>
                      <td className="px-4 py-3"><AvailBadge availability={p.availability} /></td>
                      <td className="px-4 py-3 text-right whitespace-nowrap space-x-3">
                        <button onClick={() => setEditing(p)} className="text-emerald-400 hover:text-emerald-300 text-xs font-semibold">Edit</button>
                        <button onClick={() => handleDuplicate(p._id)} className="text-cyan-400 hover:text-cyan-300 text-xs font-semibold">Duplicate</button>
                        <button onClick={() => handleToggleActive(p)} className="text-white/50 hover:text-white text-xs font-semibold">
                          {p.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                        <button onClick={() => handleDelete(p._id)} className="text-rose-400 hover:text-rose-300 text-xs font-semibold">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {items.map((p, i) => (
                <div key={p._id} className={`rounded-2xl bg-[#14141C] ring-1 ring-white/10 p-4 ${p.status === 'inactive' ? 'opacity-40' : ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold">{p.productName}</p>
                      <p className="text-xs text-white/40 mt-0.5">{p.category}</p>
                    </div>
                    <div className="flex flex-col gap-0.5 shrink-0">
                      <button onClick={() => move(i, -1)} disabled={i === 0} className="text-white/30 hover:text-white disabled:opacity-20 text-xs">▲</button>
                      <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-white/30 hover:text-white disabled:opacity-20 text-xs">▼</button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-3">
                    <PriceCell price={p.price} offerPrice={p.offerPrice} />
                    <AvailBadge availability={p.availability} />
                  </div>
                  {p.offerLabel && <span className="inline-block mt-2 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30">{p.offerLabel}</span>}

                  <div className="grid grid-cols-2 gap-2 mt-4">
                    <button onClick={() => setEditing(p)} className="py-2 rounded-xl bg-white/5 ring-1 ring-white/10 text-xs font-semibold text-white/80">Edit</button>
                    <button onClick={() => handleDuplicate(p._id)} className="py-2 rounded-xl bg-cyan-500/10 ring-1 ring-cyan-500/30 text-xs font-semibold text-cyan-400">Duplicate</button>
                    <button onClick={() => handleToggleActive(p)} className="py-2 rounded-xl bg-white/5 ring-1 ring-white/10 text-xs font-semibold text-white/70">
                      {p.status === 'active' ? 'Deactivate' : 'Activate'}
                    </button>
                    <button onClick={() => handleDelete(p._id)} className="py-2 rounded-xl bg-rose-500/10 ring-1 ring-rose-500/30 text-xs font-semibold text-rose-400">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {editing && (
          <MiniERPPriceFlowProductForm initial={editing === 'new' ? null : editing} onSave={handleSave} onClose={() => setEditing(null)} />
        )}
      </div>
    </div>
  );
}
